import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import ReactDOM from "react-dom";
import {
  formatLocalDate,
  getTodayString,
} from "../utils/timezone";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { toast } from "react-toastify";
import CategoryManager from "../components/CategoryManager";
import TemplateSetup from "../components/TemplateSetup";
import DayCard from "../components/DayCard";
import Analytics from "../components/Analytics";
import TodoList from "../components/TodoList";
import taskService from "../services/taskService";
import categoryService from "../services/categoryService";
import templateService from "../services/templateService";
import sleepService from "../services/sleepService";
import todoService from "../services/todoService";
import notificationService from "../services/notificationService";
import calendarService from "../services/calendarService";
import keepAliveService from "../services/keepAliveService";
import Onboarding from "../components/Onboarding";
import Seo from "../components/Seo";
import AssistantPanel from "../components/AssistantPanel";
import LiveInsightBanner from "../components/LiveInsightBanner";
import "./Dashboard.css";

const Dashboard = () => {
  const navigate = useNavigate();
  const { logout: contextLogout } = useContext(AuthContext);

  // Helper function to get local date string (not UTC)
  const getLocalDateString = (dateInput) => {
    return formatLocalDate(dateInput);
  };

  // Calculate current week on component mount
  const getCurrentWeek = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const day = now.getDate();

    // Calculate week number (Week 1: 1-7, Week 2: 8-14, Week 3: 15-21, Week 4: 22-end)
    // Always limit to 4 weeks per month
    const week = Math.min(Math.ceil(day / 7), 4);

    return { year, month, week };
  };

  // Get today's date string for the date input (always a real date, never "")
  const getTodayDateString = () => {
    return getTodayString();
  };

  // Convert date object to week selection
  const dateToWeekSelection = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    // Always limit to 4 weeks per month
    const week = Math.min(Math.ceil(day / 7), 4);

    return { year, month, week };
  };

  const handleDateChange = (e) => {
    const dateString = e.target.value;
    if (dateString) {
      const newSelection = dateToWeekSelection(dateString);
      setSelectedDate(newSelection);
      setDisplayDate(dateString);
      setSelectedDayDate(dateString); // Update day view selected date
      setScrollToDate(dateString);
    }
  };

  const [selectedDate, setSelectedDate] = useState(getCurrentWeek());
  const [displayDate, setDisplayDate] = useState(getTodayDateString());
  const [selectedDayDate, setSelectedDayDate] = useState(getTodayDateString()); // For day view
  const [scrollToDate, setScrollToDate] = useState(null);
  const dayCardRefs = useRef({});
  const [tasks, setTasks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  // Specific loading states for button disabling
  const [addingTask, setAddingTask] = useState(false);
  const [deletingTask, setDeletingTask] = useState({});
  const [addingCategory, setAddingCategory] = useState(false);
  const [updatingCategory, setUpdatingCategory] = useState({});
  const [deletingCategory, setDeletingCategory] = useState({});
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [updatingTemplate, setUpdatingTemplate] = useState({});
  const [deletingTemplate, setDeletingTemplate] = useState({});
  const [resettingWeek, setResettingWeek] = useState(false);
  const [addingTodo, setAddingTodo] = useState(false);
  const [togglingTodo, setTogglingTodo] = useState({});
  const [deletingTodo, setDeletingTodo] = useState({});
  const [expandedPanel, setExpandedPanel] = useState(null); // null | 'tasks' | 'todos'

  const [activeTab, setActiveTab] = useState("week"); // week, categories, template, analytics
  const [viewMode, setViewMode] = useState("day"); // day or week
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsType, setAnalyticsType] = useState("week"); // week, month, category
  const [selectedAnalyticsCategory, setSelectedAnalyticsCategory] =
    useState("");
  const [selectedAnalyticsYear, setSelectedAnalyticsYear] = useState(
    new Date().getFullYear(),
  );
  const [selectedAnalyticsMonth, setSelectedAnalyticsMonth] = useState(
    new Date().getMonth(),
  );
  const [selectedAnalyticsWeek, setSelectedAnalyticsWeek] = useState(
    Math.min(Math.ceil(new Date().getDate() / 7), 4), // Cap at 4 — days 29-31 are week 4
  );
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplateForApply, setSelectedTemplateForApply] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sleepMode, setSleepMode] = useState(false);
  const [activeSleepSession, setActiveSleepSession] = useState(null);
  const [sleepDuration, setSleepDuration] = useState(0);
  const [todos, setTodos] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [user] = useState(() => {
    try {
      const storedUser = localStorage.getItem("user");
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      localStorage.removeItem("user");
      return null;
    }
  });
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("theme") || "light";
  });

  // Check if user needs onboarding (first time) - using database value
  useEffect(() => {
    let user = null;
    try {
      user = JSON.parse(localStorage.getItem("user"));
    } catch {
      localStorage.removeItem("user");
    }
    const isNewUser = sessionStorage.getItem("isNewRegistration");

    // Show onboarding for new registrations or if never completed (from database)
    if (isNewUser === "true" || (user && !user.onboardingComplete)) {
      setShowOnboarding(true);
      sessionStorage.removeItem("isNewRegistration");
    }
  }, []);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  // Function to manually start the tour
  const startTour = () => {
    setShowOnboarding(true);
  };

  // Theme toggle function
  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  // Apply theme class to document
  useEffect(() => {
    document.body.setAttribute("data-theme", theme);
  }, [theme]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileMenuOpen &&
        !event.target.closest(".profile-dropdown-container")
      ) {
        setProfileMenuOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [profileMenuOpen]);

  // Keep serverless function warm - aggressive ping strategy to prevent cold starts
  useEffect(() => {
    // Start the keep-alive service
    keepAliveService.start();

    // Cleanup on unmount
    return () => {
      keepAliveService.stop();
    };
  }, []);

  // Load todos from API
  const loadTodos = useCallback(async () => {
    try {
      const data = await todoService.getTodos();
      setTodos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.log("Failed to load todos:", error.message);
      setTodos([]);
    }
  }, []);

  // Check Google Calendar connection status
  const checkCalendarConnection = async () => {
    try {
      const status = await calendarService.getStatus();
      setGoogleCalendarConnected(status.connected);
    } catch (error) {
      console.log("Failed to check calendar status:", error.message);
    }
  };

  // Connect Google Calendar
  const handleConnectCalendar = async () => {
    try {
      const { authUrl } = await calendarService.getAuthUrl();
      // Open OAuth popup
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        "Google Calendar Authorization",
        `width=${width},height=${height},left=${left},top=${top}`,
      );
      if (!popup) {
        toast.error("Popup blocked. Allow popups to connect calendar.");
        return;
      }

      // Listen for OAuth callback
      const handleMessage = async (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === "GOOGLE_CALENDAR_CALLBACK") {
          window.removeEventListener("message", handleMessage);
          popup?.close();

          if (event.data.code) {
            try {
              await calendarService.handleCallback(event.data.code);
              setGoogleCalendarConnected(true);
              toast.success("📅 Google Calendar connected!");
            } catch (error) {
              toast.error("Failed to connect Google Calendar");
            }
          }
        }
      };
      window.addEventListener("message", handleMessage);
    } catch (error) {
      toast.error("Failed to connect Google Calendar");
      console.error("Calendar connect error:", error);
    }
  };

  // Disconnect Google Calendar
  const handleDisconnectCalendar = async () => {
    try {
      await calendarService.disconnect();
      setGoogleCalendarConnected(false);
      toast.success("Google Calendar disconnected");
    } catch (error) {
      toast.error("Failed to disconnect calendar");
    }
  };

  useEffect(() => {
    loadCategories();
    loadTemplates();
    checkActiveSleep();
    loadTodos();
    checkCalendarConnection();

    // Request notification permission on mount
    notificationService.requestPermission();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedDate) {
      loadTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Start notification monitoring when tasks change
  useEffect(() => {
    if (tasks.length > 0) {
      notificationService.startMonitoring(tasks, (task) => {
        console.log("Notification sent for task:", task.name);
      });
    }

    return () => {
      notificationService.stopMonitoring();
    };
  }, [tasks]);

  const loadCategories = useCallback(async () => {
    try {
      const data = await categoryService.getCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Failed to load categories");
      setCategories([]);
      if (error.response?.status === 401) {
        navigate("/login");
      }
    }
  }, [navigate]);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await templateService.getTemplates();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.log("Failed to load templates:", error);
      setTemplates([]);
    }
  }, []);

  const loadTasks = useCallback(async ({ silent = false } = {}) => {
    if (!selectedDate || selectedDate.month === null || !selectedDate.week) {
      console.log("Cannot load tasks: incomplete date selection", selectedDate);
      return;
    }

    // Silent refreshes re-sync in the background without swapping the whole
    // week view for the "Loading tasks..." screen.
    if (!silent) setLoading(true);
    try {
      console.log("Loading tasks for:", selectedDate);
      const data = await taskService.getTasksByWeek(
        selectedDate.year,
        selectedDate.month,
        selectedDate.week,
      );
      console.log("Tasks loaded:", data);
      setTasks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Load tasks error:", error.response || error);
      toast.error(error.response?.data?.message || "Failed to load tasks");
      // Keep whatever is on screen during a silent refresh; only clear on a
      // full load so the view never blanks out.
      if (!silent) setTasks([]);
      if (error.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedDate, navigate]);

  // Scroll to specific day card when scrollToDate is set
  useEffect(() => {
    if (scrollToDate && !loading && dayCardRefs.current[scrollToDate]) {
      setTimeout(() => {
        dayCardRefs.current[scrollToDate]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        setScrollToDate(null);
      }, 100);
    }
  }, [scrollToDate, loading]);

  const loadAnalytics = useCallback(async () => {
    if (!selectedDate) return;

    try {
      let data;
      if (analyticsType === "week") {
        console.log("Loading week analytics:", selectedDate);
        data = await taskService.getWeeklyAnalytics(
          selectedDate.year,
          selectedDate.month,
          selectedDate.week,
        );
      } else if (analyticsType === "month") {
        console.log("Loading month analytics:", selectedDate);
        data = await taskService.getMonthlyAnalytics(
          selectedDate.year,
          selectedDate.month,
        );
      } else if (analyticsType === "category" && selectedAnalyticsCategory) {
        console.log("Loading category analytics:", selectedAnalyticsCategory);
        data = await taskService.getCategoryAnalytics(
          selectedAnalyticsCategory,
        );
      }
      console.log("Analytics data loaded:", data);
      setAnalyticsData(data || null);
    } catch (error) {
      console.error("Analytics error:", error);
      setAnalyticsData(null);
      toast.error(
        "Failed to load analytics: " +
          (error.response?.data?.message || error.message),
      );
    }
  }, [selectedDate, analyticsType, selectedAnalyticsCategory]);

  const handleAssistantRefresh = async () => {
    await loadTasks();
    await loadTodos();
    await loadTemplates();

    if (activeTab === "analytics") {
      await loadAnalytics();
    }
  };

  useEffect(() => {
    if (activeTab === "analytics") {
      loadAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, analyticsType, selectedAnalyticsCategory, selectedDate]);

  // Helper function to get max weeks in a month
  const getMaxWeeksInMonth = (year, month) => {
    // Always return 4 weeks per month (Week 4 covers days 22 to end of month)
    return 4;
  };

  // Handle analytics period changes
  const handleAnalyticsYearChange = (year) => {
    setSelectedAnalyticsYear(year);
    setSelectedDate({
      year,
      month: selectedAnalyticsMonth,
      week: selectedAnalyticsWeek,
    });
  };

  const handleAnalyticsMonthChange = (month) => {
    setSelectedAnalyticsMonth(month);
    const maxWeeks = getMaxWeeksInMonth(selectedAnalyticsYear, month);
    const safeWeek = Math.min(selectedAnalyticsWeek, maxWeeks);
    setSelectedAnalyticsWeek(safeWeek);
    setSelectedDate({ year: selectedAnalyticsYear, month, week: safeWeek });
  };

  const handleAnalyticsWeekChange = (week) => {
    setSelectedAnalyticsWeek(week);
    setSelectedDate({
      year: selectedAnalyticsYear,
      month: selectedAnalyticsMonth,
      week,
    });
  };

  const navigateWeek = (direction) => {
    if (!selectedDate || selectedDate.month === null || !selectedDate.week) {
      return;
    }

    const { year, month, week } = selectedDate;
    const maxWeeks = 4; // Always 4 weeks per month

    let newWeek = week + direction;
    let newMonth = month;
    let newYear = year;

    // Move to next month if week exceeds max
    if (newWeek > maxWeeks) {
      newWeek = 1;
      newMonth++;
      if (newMonth > 11) {
        newMonth = 0;
        newYear++;
      }
    }
    // Move to previous month if week is less than 1
    else if (newWeek < 1) {
      newMonth--;
      if (newMonth < 0) {
        newMonth = 11;
        newYear--;
      }
      newWeek = 4; // Always 4 weeks per month
    }

    setSelectedDate({ year: newYear, month: newMonth, week: newWeek });

    // Update display date to first day of the new week
    const firstDayOfWeek = 1 + (newWeek - 1) * 7;
    const newDate = new Date(newYear, newMonth, firstDayOfWeek);
    setDisplayDate(getLocalDateString(newDate));
  };

  const navigateDay = (direction) => {
    const currentDate = new Date(selectedDayDate);
    currentDate.setDate(currentDate.getDate() + direction);

    const newDateString = getLocalDateString(currentDate);
    setSelectedDayDate(newDateString);
    setDisplayDate(newDateString);

    // Update week selection for the new date
    const newSelection = dateToWeekSelection(newDateString);
    setSelectedDate(newSelection);
  };

  const handleAddTask = async (
    date,
    name,
    categoryId,
    plannedTime = 0,
    isAutomated = false,
    scheduledStartTime = null,
    scheduledEndTime = null,
  ) => {
    if (addingTask) return;
    setAddingTask(true);
    try {
      await taskService.createTask({
        name,
        date,
        category: categoryId,
        plannedTime,
        isAutomated,
        scheduledStartTime,
        scheduledEndTime,
      });
      toast.success("Task added successfully");

      // Reload tasks only if we have a complete selectedDate
      if (
        selectedDate?.year &&
        selectedDate?.month !== null &&
        selectedDate?.week
      ) {
        await loadTasks();
      }
    } catch (error) {
      toast.error("Failed to add task");
      console.error("Add task error:", error);
    } finally {
      setAddingTask(false);
    }
  };

  const handleToggleTask = async (taskId, isActive, refreshOnly = false) => {
    // Refresh-only path (edit modal save, manual finish): re-sync from
    // server without any toggle side effects. Silent so the week view keeps
    // its content instead of flashing to "Loading tasks...".
    if (refreshOnly) {
      await loadTasks({ silent: true });
      return;
    }

    try {
      // If trying to start a task while sleep mode is active, prompt to turn off sleep
      if (isActive && sleepMode) {
        const shouldWakeUp = window.confirm(
          "😴 Sleep mode is currently active!\n\nYou cannot start tasks while sleeping. Would you like to turn off sleep mode and start this task?",
        );

        if (shouldWakeUp) {
          await toggleSleepMode(); // Turn off sleep mode
        } else {
          return; // Don't start the task
        }
      }

      const targetTask = tasks.find((task) => task._id === taskId);
      if (!targetTask) return;

      // If trying to start a task, check if another task is running
      if (isActive) {
        const currentlyActiveTask = tasks.find(
          (task) => task.isActive && task._id !== taskId,
        );

        if (currentlyActiveTask) {
          const shouldStop = window.confirm(
            `"${currentlyActiveTask.name}" is currently running. Do you want to stop it and start this task?\n\nClick OK to stop the previous task, or Cancel to keep both running.`,
          );

          if (shouldStop) {
            // Patch locally first, revert just this task if the server rejects
            setTasks((prev) =>
              prev.map((t) =>
                t._id === currentlyActiveTask._id
                  ? { ...t, isActive: false }
                  : t,
              ),
            );
            try {
              const stopped = await taskService.updateTask(
                currentlyActiveTask._id,
                { isActive: false },
              );
              setTasks((prev) =>
                prev.map((t) =>
                  t._id === currentlyActiveTask._id ? stopped : t,
                ),
              );
            } catch {
              setTasks((prev) =>
                prev.map((t) =>
                  t._id === currentlyActiveTask._id ? currentlyActiveTask : t,
                ),
              );
            }
          }
        }
      }

      // Optimistic toggle: update this one task instantly, then confirm with
      // the server. Never refetch the week here — that global refetch + the
      // loading flag is what made the page feel like it was reloading.
      setTasks((prev) =>
        prev.map((t) => {
          if (t._id !== taskId) return t;
          const optimistic = { ...t, isActive };
          if (isActive) {
            optimistic.startTime = t.startTime || new Date().toISOString();
          } else if (t.isActive && t.startTime) {
            optimistic.totalTime =
              (t.totalTime || 0) +
              Math.max(0, Date.now() - new Date(t.startTime).getTime());
            optimistic.startTime = null;
          }
          return optimistic;
        }),
      );

      try {
        const updated = await taskService.updateTask(taskId, { isActive });
        setTasks((prev) => prev.map((t) => (t._id === taskId ? updated : t)));
        toast.success(isActive ? "Task started" : "Task stopped");
      } catch (error) {
        // Revert only the affected task instead of refetching everything
        setTasks((prev) =>
          prev.map((t) => (t._id === taskId ? targetTask : t)),
        );
        toast.error(
          error.response?.data?.message ||
            "Couldn't save task state — reverted",
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update task");
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (deletingTask[taskId]) return;
    setDeletingTask((prev) => ({ ...prev, [taskId]: true }));
    // Snapshot the task before the async call so undo can restore it
    const deletedTask = tasks.find((t) => t._id === taskId);
    try {
      await taskService.deleteTask(taskId);
      // Functional updater prevents stale-closure bug when state changes during the await
      setTasks((prev) => prev.filter((task) => task._id !== taskId));
      // Show undo toast — `taskId` and `deletedTask` are safe snapshot values
      toast.info(
        ({ closeToast }) => (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span>Task deleted</span>
            <button
              onClick={async () => {
                closeToast();
                try {
                  await taskService.restoreTask(taskId);
                  setTasks((prev) => [...prev, deletedTask]);
                  toast.success("Task restored!");
                } catch {
                  toast.error("Failed to restore task");
                }
              }}
              style={{
                background: "#6366f1",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "4px 12px",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Undo
            </button>
          </div>
        ),
        { autoClose: 5000 },
      );
    } catch (error) {
      toast.error("Failed to delete task");
    } finally {
      setDeletingTask((prev) => {
        const newState = { ...prev };
        delete newState[taskId];
        return newState;
      });
    }
  };

  const handleDeleteDayTasks = async (date) => {
    if (
      !window.confirm(
        `Delete all tasks for ${new Date(date + "T00:00:00").toLocaleDateString()}?`,
      )
    ) {
      return;
    }

    try {
      await taskService.deleteTasksByDay(date);
      // task.date from MongoDB is a full ISO datetime string (e.g. "2026-06-15T06:30:00.000Z")
      // `date` prop is YYYY-MM-DD — must normalise both sides before comparing
      setTasks((prev) =>
        prev.filter((task) => formatLocalDate(task.date) !== date)
      );
      toast.success("Day tasks deleted successfully");
      // Reload from server as a safety net to guarantee UI is fully in sync
      await loadTasks();
    } catch (error) {
      toast.error("Failed to delete day tasks");
    }
  };

  const handleResetWeek = async () => {
    if (resettingWeek) return;
    if (
      !window.confirm("Delete all tasks for this week? This cannot be undone!")
    ) {
      return;
    }

    setResettingWeek(true);
    try {
      const { year, month, week } = selectedDate;
      await taskService.deleteTasksByWeek(year, month, week);
      await loadTasks();
      toast.success("Week reset successfully");
    } catch (error) {
      toast.error("Failed to reset week");
    } finally {
      setResettingWeek(false);
    }
  };

  const handleToggleNotification = async (
    taskId,
    enabled,
    notificationTime,
  ) => {
    try {
      // Request permission if enabling and don't have it
      if (enabled && !notificationService.hasPermission()) {
        const granted = await notificationService.requestPermission();
        if (!granted) {
          toast.error("Notification permission denied");
          return;
        }
      }

      const updateData = { notificationsEnabled: enabled };

      // Update notification time if provided
      if (notificationTime !== undefined) {
        updateData.notificationTime = notificationTime;
      }

      await taskService.updateTask(taskId, updateData);

      // Update local state
      setTasks(
        tasks.map((task) =>
          task._id === taskId ? { ...task, ...updateData } : task,
        ),
      );

      if (enabled) {
        const timeLabel = notificationTime || tasks.notificationTime || 30;
        toast.success(`Notifications enabled (${timeLabel} min before)`);
      } else {
        toast.success("Notifications disabled");
      }
    } catch (error) {
      toast.error("Failed to update notification settings");
    }
  };

  const handleReorderTasks = async (date, reorderedTasks) => {
    // Update local state immediately for smooth UX
    // Replace all tasks for the specific date with reordered tasks
    const updatedTasks = tasks.map((task) => {
      const reorderedTask = reorderedTasks.find((t) => t._id === task._id);
      if (reorderedTask) {
        return {
          ...reorderedTask,
          order: reorderedTasks.indexOf(reorderedTask),
        };
      }
      return task;
    });

    // Sort tasks by date and order
    updatedTasks.sort((a, b) => {
      const dateCompare = new Date(a.date) - new Date(b.date);
      if (dateCompare !== 0) return dateCompare;
      return (a.order || 0) - (b.order || 0);
    });

    setTasks(updatedTasks);

    // Update order in backend for each task
    try {
      const updates = reorderedTasks.map((task, index) =>
        taskService.updateTask(task._id, { order: index }),
      );
      await Promise.all(updates);
    } catch (error) {
      console.error("Failed to update task order:", error);
      // Reload tasks to sync with server
      await loadTasks();
    }
  };

  const handleApplyTemplate = async (templateIdFromProp) => {
    if (!selectedDate) {
      toast.error("Please select a week first");
      return;
    }

    if (templates.length === 0) {
      toast.error("No templates available. Create one first!");
      return;
    }

    const templateIdToApply = typeof templateIdFromProp === 'string' 
      ? templateIdFromProp 
      : selectedTemplateForApply;

    if (!templateIdToApply) {
      setShowTemplateModal(true);
      return;
    }

    // Prevent multiple simultaneous applications
    if (loading) {
      toast.info("Template is already being applied...");
      return;
    }

    setLoading(true);
    try {
      const result = await templateService.applyTemplate(
        templateIdToApply,
        selectedDate.year,
        selectedDate.month,
        selectedDate.week,
      );
      const appliedTasks = result?.tasks || [];
      const appliedTodos = result?.todos || [];
      const created = result?.calendarEventsCreated || 0;
      const skipped = result?.calendarSkipped || [];
      const dateSkipped = result?.dateSkipped || [];
      const skipLabel = (s) =>
        s.day ? `${s.name} (${s.day.slice(0, 3)})` : s.name;
      toast.success(
        `Template applied: ${appliedTasks.length} task(s), ${appliedTodos.length} todo(s)${created > 0 ? ` • 📅 ${created} reminder${created !== 1 ? "s" : ""} added` : ""}`,
      );
      // Sanity check: reminders were requested but none landed in Google.
      const expectedReminders =
        (selectedTemplateForApply &&
          templates.find((t) => t._id === selectedTemplateForApply)
          ? (() => {
              const st = templates.find((t) => t._id === selectedTemplateForApply);
              return (
                (st.tasks || []).filter((t) => t.addToCalendar).length +
                (st.quickTodos || []).filter((t) => (t.reminderMinutes || 0) > 0).length
              );
            })()
          : skipped.length + created);
      if (expectedReminders > 0 && created === 0 && skipped.length === 0) {
        toast.error(
          "⚠️ Reminders were requested but none reached Google Calendar. Reconnect calendar from the profile menu and re-apply.",
          { autoClose: 9000 },
        );
      }
      if (dateSkipped.length > 0) {
        toast.info(
          `⏭️ No dates left this week for: ${dateSkipped.slice(0, 4).map(skipLabel).join(", ")}${dateSkipped.length > 4 ? ", …" : ""} — apply earlier in the week for full coverage.`,
          { autoClose: 9000 },
        );
      }
      if (skipped.length > 0) {
        const firstErr = skipped.find((s) => s.reason !== "calendar_not_connected" && s.detail);
        if (result?.calendarConnected === false) {
          toast.warn(
            "⚠️ Google Calendar not connected — reminders were skipped. Connect from the profile menu and re-apply.",
            { autoClose: 8000 },
          );
        } else {
          toast.warn(
            `⚠️ ${skipped.length} reminder${skipped.length !== 1 ? "s" : ""} skipped (${skipped.slice(0, 3).map(skipLabel).join(", ")}${skipped.length > 3 ? ", …" : ""})${firstErr?.detail ? ` — Google said: ${firstErr.detail}` : ""}`,
            { autoClose: 9000 },
          );
        }
      }
      setShowTemplateModal(false);
      setSelectedTemplateForApply("");
      await loadTasks();
      await loadTodos();
      // In day view, if the current day got nothing but later days did
      // (e.g. applying on Sunday), jump to the first day with new tasks
      // instead of leaving an empty "No tasks" screen.
      if (viewMode === "day" && appliedTasks.length > 0) {
        const createdDates = [
          ...new Set(
            appliedTasks
              .map((t) => formatLocalDate(t.date))
              .filter(Boolean),
          ),
        ].sort();
        if (
          createdDates.length > 0 &&
          !createdDates.includes(selectedDayDate)
        ) {
          setDisplayDate(createdDates[0]);
          setSelectedDayDate(createdDates[0]);
          setScrollToDate(createdDates[0]);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to apply template");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (templateData) => {
    if (creatingTemplate) return;
    setCreatingTemplate(true);
    try {
      await templateService.createTemplate(templateData);
      toast.success("Template created successfully");
      await loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create template");
      throw error; // Re-throw to inform caller
    } finally {
      setCreatingTemplate(false);
    }
  };

  const handleUpdateTemplate = async (templateId, templateData) => {
    if (updatingTemplate[templateId]) return;
    setUpdatingTemplate((prev) => ({ ...prev, [templateId]: true }));
    try {
      await templateService.updateTemplate(templateId, templateData);
      toast.success("Template updated successfully");
      await loadTemplates(); // Wait for templates to reload
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update template");
      throw error; // Re-throw to inform caller
    } finally {
      setUpdatingTemplate((prev) => {
        const newState = { ...prev };
        delete newState[templateId];
        return newState;
      });
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (deletingTemplate[templateId]) return;
    setDeletingTemplate((prev) => ({ ...prev, [templateId]: true }));
    try {
      await templateService.deleteTemplate(templateId);
      toast.success("Template deleted successfully");
      loadTemplates();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete template");
    } finally {
      setDeletingTemplate((prev) => {
        const newState = { ...prev };
        delete newState[templateId];
        return newState;
      });
    }
  };

  const handleAddCategory = async (categoryData) => {
    if (addingCategory) return;
    setAddingCategory(true);
    try {
      await categoryService.createCategory(categoryData);
      toast.success("Category created successfully");
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create category");
    } finally {
      setAddingCategory(false);
    }
  };

  const handleUpdateCategory = async (categoryId, categoryData) => {
    if (updatingCategory[categoryId]) return;
    setUpdatingCategory((prev) => ({ ...prev, [categoryId]: true }));
    try {
      await categoryService.updateCategory(categoryId, categoryData);
      toast.success("Category updated successfully");
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update category");
    } finally {
      setUpdatingCategory((prev) => {
        const newState = { ...prev };
        delete newState[categoryId];
        return newState;
      });
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (deletingCategory[categoryId]) return;
    setDeletingCategory((prev) => ({ ...prev, [categoryId]: true }));
    try {
      await categoryService.deleteCategory(categoryId);
      toast.success("Category deleted successfully");
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete category");
    } finally {
      setDeletingCategory((prev) => {
        const newState = { ...prev };
        delete newState[categoryId];
        return newState;
      });
    }
  };

  const handleLogout = () => {
    try {
      contextLogout();
    } catch {
      localStorage.removeItem("user");
    }
    navigate("/login");
    toast.success("Logged out successfully");
  };

  // Sleep mode functions
  const checkActiveSleep = async () => {
    try {
      const activeSession = await sleepService.getActiveSleep();
      if (activeSession) {
        setActiveSleepSession(activeSession);
        setSleepMode(true);
      }
    } catch (error) {
      // Silently fail - sleep feature is optional
      console.log("Sleep tracking not available:", error.message);
    }
  };

  const toggleSleepMode = async () => {
    try {
      if (sleepMode) {
        // Stop sleep
        const stoppedSession = await sleepService.stopSleep();
        const duration = Math.floor(stoppedSession.duration / 1000 / 60); // minutes
        toast.success(`Sleep ended: ${duration} minutes tracked`);
        setSleepMode(false);
        setActiveSleepSession(null);
        setSleepDuration(0);
        loadTasks({ silent: true }); // background sync, no full-page flash
      } else {
        // Pause all active tasks before starting sleep
        const activeTasks = tasks.filter((task) => task.isActive);
        if (activeTasks.length > 0) {
          toast.info(`Pausing ${activeTasks.length} active task(s)...`);
          try {
            for (const task of activeTasks) {
              const stopped = await taskService.stopTask(task._id);
              setTasks((prev) =>
                prev.map((t) => (t._id === task._id ? stopped : t)),
              );
            }
          } catch (error) {
            console.error("Error pausing tasks:", error);
            toast.error("Failed to pause some tasks");
            loadTasks({ silent: true });
            return; // Don't start sleep if pausing failed
          }
        }

        // Start sleep
        const newSession = await sleepService.startSleep();
        setActiveSleepSession(newSession);
        setSleepMode(true);
        toast.success("Sleep mode activated 😴");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to toggle sleep mode",
      );
    }
  };

  // Update sleep duration every minute when sleep is active
  useEffect(() => {
    let interval;
    if (sleepMode && activeSleepSession) {
      interval = setInterval(() => {
        const elapsed =
          Date.now() - new Date(activeSleepSession.startTime).getTime();
        setSleepDuration(Math.floor(elapsed / 1000 / 60)); // minutes
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [sleepMode, activeSleepSession]);

  // Group tasks by date
  const getWeekDays = () => {
    if (!selectedDate || selectedDate.month === null || !selectedDate.week)
      return [];

    const { year, month, week } = selectedDate;

    const startDay = 1 + (week - 1) * 7;

    // For weeks 1-3: always 7 days (startDay to startDay+6).
    // For week 4: extend to the actual last day of the month so days 29, 30, 31
    // are included — they were previously cut off by the hardcoded `i < 7` loop.
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate(); // day-0 of next month = last day of this month
    const endDay = week === 4 ? lastDayOfMonth : startDay + 6;

    const days = [];
    const dayNames = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];

    for (let dayNum = startDay; dayNum <= endDay; dayNum++) {
      const date = new Date(year, month, dayNum, 12, 0, 0);

      // Format date as YYYY-MM-DD without timezone conversion
      const yr = date.getFullYear();
      const mo = String(date.getMonth() + 1).padStart(2, "0");
      const dy = String(date.getDate()).padStart(2, "0");
      const dateString = `${yr}-${mo}-${dy}`;

      const dayOfWeek = date.getDay();
      days.push({
        date: dateString,
        dayName: dayNames[dayOfWeek],
        displayName: `${dayNames[dayOfWeek]} (${date.getDate()}/${
          date.getMonth() + 1
        })`,
      });
    }

    return days;
  };

  const weekDays = getWeekDays();
  const groupedTasks = weekDays.reduce((acc, day) => {
    acc[day.date] = tasks.filter((task) => {
      const taskDate = formatLocalDate(task.date);
      return taskDate === day.date;
    });
    return acc;
  }, {});

  console.log("Tasks:", tasks);
  console.log("Week Days:", weekDays);
  console.log("Grouped Tasks:", groupedTasks);

  // Todo handlers - API-based
  const handleAddTodo = async (text, deadline = null) => {
    if (addingTodo) return;
    setAddingTodo(true);
    try {
      const newTodo = await todoService.createTodo(text, deadline);
      // Use functional updater to avoid stale closure on todos
      setTodos((prev) => [...prev, newTodo]);
      return newTodo; // Return so callers can use the created todo (e.g., for reminders)
    } catch (error) {
      toast.error("Failed to add todo");
    } finally {
      setAddingTodo(false);
    }
  };

  const handleToggleTodo = async (id) => {
    if (togglingTodo[id]) return;
    setTogglingTodo((prev) => ({ ...prev, [id]: true }));
    try {
      // Read current todo state at call time to get its completed value
      const todo = todos.find((t) => t._id === id);
      if (todo) {
        const updated = await todoService.toggleTodo(id, !todo.completed);
        // Functional updater avoids stale closure on todos
        setTodos((prev) => prev.map((t) => (t._id === id ? updated : t)));
      }
    } catch (error) {
      toast.error("Failed to update todo");
    } finally {
      setTogglingTodo((prev) => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
    }
  };

  const handleDeleteAllTodos = async () => {
    if (todos.length === 0) {
      toast.info("No todos to delete.");
      return;
    }
    if (!window.confirm(`Delete all ${todos.length} todo(s)? This cannot be undone.`)) return;
    try {
      await todoService.deleteAll();
      setTodos([]);
      // Reload from server to confirm server state matches
      await loadTodos();
      toast.success("All todos deleted!");
    } catch (error) {
      console.error("Delete all todos error:", error);
      toast.error("Failed to delete all todos");
      await loadTodos(); // Re-sync with server on failure too
    }
  };

  const handleClearCompletedTodos = async () => {
    const completedCount = todos.filter((t) => t.completed).length;
    if (completedCount === 0) {
      toast.info("No completed todos to clear.");
      return;
    }
    if (!window.confirm(`Clear ${completedCount} completed todo(s)?`)) return;
    try {
      await todoService.clearCompleted();
      // Reload from server to confirm server state
      await loadTodos();
      toast.success(`${completedCount} completed todo(s) cleared!`);
    } catch (error) {
      console.error("Clear completed error:", error);
      toast.error("Failed to clear completed todos");
      await loadTodos();
    }
  };

  const handleEditTodo = async (id, text) => {
    const trimmed = (text || "").trim();
    if (!trimmed) {
      toast.warn("Todo text can't be empty");
      return;
    }
    try {
      const updated = await todoService.updateTodo(id, { text: trimmed });
      setTodos((prev) => prev.map((t) => (t._id === id ? updated : t)));
      toast.success("Todo updated");
    } catch (error) {
      toast.error("Failed to update todo");
      await loadTodos();
      throw error;
    }
  };

  const handleTaskCalendarChange = (taskId, eventId, extra = {}) => {
    setTasks((prev) =>
      prev.map((t) =>
        t._id === taskId ? { ...t, calendarEventId: eventId, ...extra } : t,
      ),
    );
  };

  const handleTodoCalendarChange = (todoId, eventId) => {
    setTodos((prev) =>
      prev.map((t) => (t._id === todoId ? { ...t, calendarEventId: eventId } : t)),
    );
  };

  const handleDeleteTodo = async (id) => {    if (deletingTodo[id]) return;
    setDeletingTodo((prev) => ({ ...prev, [id]: true }));
    // Capture the todo snapshot before the async call for undo support
    const deletedTodo = todos.find((t) => t._id === id);
    try {
      await todoService.deleteTodo(id);
      // Functional updater prevents stale-closure bug when state changes during the await
      setTodos((prev) => prev.filter((todo) => todo._id !== id));
      // Show undo toast — closures over `id` and `deletedTodo` are safe (primitives / snapshot)
      toast.info(
        ({ closeToast }) => (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span>Todo deleted</span>
            <button
              onClick={async () => {
                closeToast();
                try {
                  await todoService.restoreTodo(id);
                  setTodos((prev) => [...prev, deletedTodo]);
                  toast.success("Todo restored!");
                } catch {
                  toast.error("Failed to restore todo");
                }
              }}
              style={{
                background: "#6366f1",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "4px 12px",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Undo
            </button>
          </div>
        ),
        { autoClose: 5000 },
      );
    } catch (error) {
      toast.error("Failed to delete todo");
    } finally {
      setDeletingTodo((prev) => {
        const newState = { ...prev };
        delete newState[id];
        return newState;
      });
    }
  };

  // Mark todo as missed / undo missed
  const handleMarkTodoMissed = async (todoId, missed) => {
    try {
      const updated = await todoService.markTodoMissed(todoId, missed);
      setTodos((prev) => prev.map((t) => (t._id === todoId ? updated : t)));
      if (!missed) toast.info("Missed mark removed — back to to-do");
      // When marking missed, the re-remind prompt in TodoList handles the toast
      // so we stay silent here to avoid double-toasting.
    } catch (error) {
      toast.error("Failed to update missed status");
    }
  };

  // Re-remind a missed todo on a new date (brings it back to the to-do list)
  const handleRescheduleTodo = async (todoId, newDate) => {
    try {
      const updated = await todoService.rescheduleTodo(todoId, newDate);
      setTodos((prev) => prev.map((t) => (t._id === todoId ? updated : t)));
      // Reload to re-apply carryover/sort from the server
      await loadTodos();
    } catch (error) {
      toast.error("Failed to set re-reminder");
      throw error;
    }
  };

  // Dismiss all missed todos at once — clears the bottom tray so a pile of
  // missed items never overshadows the need-to-do ones
  const handleClearMissedTodos = async () => {
    const missedCount = todos.filter((t) => t.missed && !t.completed).length;
    if (missedCount === 0) {
      toast.info("No missed todos to clear.");
      return;
    }
    if (!window.confirm(`Dismiss ${missedCount} missed todo(s)? They'll be removed from this list.`)) return;
    try {
      await todoService.clearMissed();
      await loadTodos();
      toast.success(`${missedCount} missed todo(s) dismissed!`);
    } catch (error) {
      console.error("Clear missed error:", error);
      toast.error("Failed to clear missed todos");
      await loadTodos();
    }
  };

  return (
    <div className={`dashboard ${sleepMode ? "sleep-mode-active" : ""}`}>
      <Seo
        title="Dashboard"
        description="Your private Task Tracker Pro dashboard."
        path="/dashboard"
        noindex
      />
      {/* Sleep Mode Overlay */}
      {sleepMode && (
        <div className="sleep-mode-overlay">
          <div className="sleep-mode-indicator">
            <span className="sleep-icon"><span className="material-symbols-outlined" style={{fontSize:'1.2rem'}}>dark_mode</span></span>
            <span className="sleep-text">Sleep Mode Active</span>
            <span className="sleep-duration">{sleepDuration} min</span>
          </div>
        </div>
      )}

      {/* Onboarding Modal for new users */}
      {showOnboarding && <Onboarding onComplete={handleOnboardingComplete} />}

      <div className="dashboard-header">
        <div className="header-left">
          {/* Hamburger menu - mobile only, on the left */}
          <button
            className="hamburger-menu"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <span className="material-symbols-outlined">close</span> : <span className="material-symbols-outlined">menu</span>}
          </button>
          <div
            className="logo-container"
            onClick={() => setActiveTab("week")}
            style={{ cursor: "pointer" }}
            title="Go to Home"
          >
            <img
              src="/logo.svg"
              alt="Tracku Logo"
              className="app-logo"
            />
            <h1>Tracku</h1>
          </div>
        </div>
        {/* Mobile right side: Sleep + Theme Toggle */}
        <div className="mobile-header-actions">
          <button
            className={`btn-sleep-mode ${sleepMode ? "active" : ""}`}
            onClick={toggleSleepMode}
            title={
              sleepMode ? `Sleep: ${sleepDuration} min` : "Start Sleep Mode"
            }
          >
            <span className="material-symbols-outlined" style={{fontSize:'1rem'}}>dark_mode</span> {sleepMode ? `${sleepDuration}m` : "Sleep"}
          </button>
          <button
            className="btn-theme-toggle"
            onClick={toggleTheme}
            title={
              theme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"
            }
          >
            <span className="material-symbols-outlined" style={{fontSize:'1rem'}}>{theme === "light" ? "dark_mode" : "light_mode"}</span>
          </button>
        </div>
        <div className="header-nav">
          <button
            className={`nav-btn ${activeTab === "week" ? "active" : ""}`}
            onClick={() => setActiveTab("week")}
          >
            <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>dashboard</span> Home
          </button>
          <button
            className={`nav-btn ${activeTab === "categories" ? "active" : ""}`}
            onClick={() => setActiveTab("categories")}
          >
            <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>label</span> Categories
          </button>
          <button
            className={`nav-btn ${activeTab === "template" ? "active" : ""}`}
            onClick={() => setActiveTab("template")}
          >
            <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>calendar_view_week</span> Template
          </button>
          <button
            className={`nav-btn ${activeTab === "analytics" ? "active" : ""}`}
            onClick={() => setActiveTab("analytics")}
          >
            <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>insights</span> Analytics
          </button>
        </div>
        <div className="desktop-header-actions desktop-only">
          <button
            className={`btn-sleep-mode ${sleepMode ? "active" : ""}`}
            onClick={toggleSleepMode}
            title={
              sleepMode ? `Sleep: ${sleepDuration} min` : "Start Sleep Mode"
            }
          >
            <span className="material-symbols-outlined" style={{fontSize:'1rem'}}>dark_mode</span> {sleepMode ? `${sleepDuration}m` : "Sleep"}
          </button>
          <button
            className="btn-theme-toggle"
            onClick={toggleTheme}
            title={
              theme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"
            }
          >
            <span className="material-symbols-outlined" style={{fontSize:'1rem'}}>{theme === "light" ? "dark_mode" : "light_mode"}</span>
          </button>
          <div className="profile-dropdown-container">
            <button
              className={`btn-profile ${profileMenuOpen ? "active" : ""}`}
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              title="Profile menu"
            >
              <span className="profile-avatar">
                {user?.name?.charAt(0)?.toUpperCase() || "👤"}
              </span>
              <span className="profile-name">
                {user?.name?.split(" ")[0] || "Profile"}
              </span>
              <span className="dropdown-arrow">
                {profileMenuOpen ? "▲" : "▼"}
              </span>
            </button>
            {profileMenuOpen && (
              <div className="profile-dropdown-menu">
                <div className="profile-dropdown-header">
                  <span className="profile-avatar-large">
                    {user?.name?.charAt(0)?.toUpperCase() || "👤"}
                  </span>
                  <div className="profile-info">
                    <span className="profile-name-full">
                      {user?.name || "User"}
                    </span>
                    <span className="profile-email">{user?.email || ""}</span>
                  </div>
                </div>
                <div className="profile-dropdown-divider"></div>
                <button
                  className="profile-dropdown-item theme-toggle"
                  onClick={() => {
                    toggleTheme();
                  }}
                >
                  <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>{theme === "light" ? "dark_mode" : "light_mode"}</span> {theme === "light" ? "Dark Mode" : "Light Mode"}
                </button>
                <button
                  className="profile-dropdown-item"
                  onClick={() => {
                    setProfileMenuOpen(false);
                    startTour();
                  }}
                >
                  <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>tour</span> Tour Guide
                </button>
                <button
                  className={`profile-dropdown-item ${googleCalendarConnected ? "connected" : ""}`}
                  onClick={() => {
                    if (googleCalendarConnected) {
                      handleDisconnectCalendar();
                    } else {
                      handleConnectCalendar();
                    }
                  }}
                >
                  <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>calendar_month</span> {googleCalendarConnected
                    ? "Disconnect Calendar"
                    : "Connect Google Calendar"}
                </button>
                <button
                  className="profile-dropdown-item logout"
                  onClick={() => {
                    setProfileMenuOpen(false);
                    handleLogout();
                  }}
                >
                  <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>logout</span> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Dropdown Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu">
          <button
            className={`mobile-menu-item ${
              activeTab === "week" ? "active" : ""
            }`}
            onClick={() => {
              setActiveTab("week");
              setMobileMenuOpen(false);
            }}
          >
            <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>dashboard</span> Home
          </button>
          <button
            className={`mobile-menu-item ${
              activeTab === "categories" ? "active" : ""
            }`}
            onClick={() => {
              setActiveTab("categories");
              setMobileMenuOpen(false);
            }}
          >
            <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>label</span> Categories
          </button>
          <button
            className={`mobile-menu-item ${
              activeTab === "template" ? "active" : ""
            }`}
            onClick={() => {
              setActiveTab("template");
              setMobileMenuOpen(false);
            }}
          >
            <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>calendar_view_week</span> Template
          </button>
          <button
            className={`mobile-menu-item ${
              activeTab === "analytics" ? "active" : ""
            }`}
            onClick={() => {
              setActiveTab("analytics");
              setMobileMenuOpen(false);
            }}
          >
            <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>insights</span> Analytics
          </button>
          <button
            className="mobile-menu-item tour-item"
            onClick={() => {
              setMobileMenuOpen(false);
              startTour();
            }}
          >
            <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>tour</span> Tour Guide
          </button>
          <button
            className={`mobile-menu-item ${googleCalendarConnected ? "connected" : ""}`}
            onClick={() => {
              setMobileMenuOpen(false);
              if (googleCalendarConnected) {
                handleDisconnectCalendar();
              } else {
                handleConnectCalendar();
              }
            }}
          >
            <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>calendar_month</span> {googleCalendarConnected
              ? "Disconnect Calendar"
              : "Connect Google Calendar"}
          </button>
          <button
            className="mobile-menu-item logout-item"
            onClick={() => {
              setMobileMenuOpen(false);
              handleLogout();
            }}
          >
            <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>logout</span> Logout
          </button>
        </div>
      )}

      <div className="dashboard-tabs">
        <button
          className={`tab-btn ${activeTab === "week" ? "active" : ""}`}
          onClick={() => setActiveTab("week")}
        >
          <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>dashboard</span> Home
        </button>
        <button
          className={`tab-btn ${activeTab === "categories" ? "active" : ""}`}
          onClick={() => setActiveTab("categories")}
        >
          <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>label</span> Categories
        </button>
        <button
          className={`tab-btn ${activeTab === "template" ? "active" : ""}`}
          onClick={() => setActiveTab("template")}
        >
          <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>calendar_view_week</span> Template
        </button>
        <button
          className={`tab-btn ${activeTab === "analytics" ? "active" : ""}`}
          onClick={() => setActiveTab("analytics")}
        >
          <span className="material-symbols-outlined" style={{fontSize:'1.1rem'}}>insights</span> Analytics
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === "week" && (
          <div className="week-view">
            {!selectedDate ||
            selectedDate.month === null ||
            !selectedDate.week ? (
              <div className="empty-state-large">
                <div className="empty-icon">📆</div>
                <h3>Select a Week to Get Started</h3>
                <p>Use the date picker in the header to choose a week</p>
              </div>
            ) : (
              <>
                <div className="week-actions">
                  <div className="view-mode-toggle">
                    <button
                      className={`view-mode-btn ${viewMode === "day" ? "active" : ""}`}
                      onClick={() => setViewMode("day")}
                      title="Day View"
                    >
                      <span className="material-symbols-outlined" style={{fontSize:'1rem'}}>today</span> Day
                    </button>
                    <button
                      className={`view-mode-btn ${viewMode === "week" ? "active" : ""}`}
                      onClick={() => setViewMode("week")}
                      title="Week View"
                    >
                      <span className="material-symbols-outlined" style={{fontSize:'1rem'}}>date_range</span> Week
                    </button>
                  </div>
                  <div className="week-navigation">
                    <button
                      className="btn-week-nav"
                      onClick={() =>
                        viewMode === "day" ? navigateDay(-1) : navigateWeek(-1)
                      }
                      title={
                        viewMode === "day" ? "Previous Day" : "Previous Week"
                      }
                    >
                      ← {viewMode === "day" ? "Previous Day" : "Previous Week"}
                    </button>
                    <input
                      type="date"
                      className="date-input-compact"
                      value={displayDate}
                      onChange={handleDateChange}
                      title="Jump to specific date"
                    />
                    <button
                      className="btn-week-nav"
                      onClick={() =>
                        viewMode === "day" ? navigateDay(1) : navigateWeek(1)
                      }
                      title={viewMode === "day" ? "Next Day" : "Next Week"}
                    >
                      {viewMode === "day" ? "Next Day" : "Next Week"} →
                    </button>
                  </div>
                  <div className="week-actions-right">
                    <button
                      className="btn-apply-template"
                      onClick={handleApplyTemplate}
                      disabled={loading}
                    >
                      {loading
                        ? "Applying..."
                        : "✨ Apply Template to This Week"}
                    </button>
                    <button
                      className="btn-reset-week"
                      onClick={handleResetWeek}
                      title="Delete all tasks for this week"
                      disabled={resettingWeek}
                    >
                      {resettingWeek ? "Resetting..." : "🗑️ Reset Week"}
                    </button>
                  </div>
                </div>
                {loading ? (
                  <div className="loading">Loading tasks...</div>
                ) : viewMode === "day" ? (
                  <div
                    className={`day-with-todos${expandedPanel ? " has-expanded" : ""}`}
                  >
                    <div
                      className={`day-section${expandedPanel === "tasks" ? " panel-expanded" : expandedPanel === "todos" ? " panel-hidden" : ""}`}
                    >
                      <div className="panel-toggle-bar">
                        <button
                          className="panel-toggle-btn"
                          title={
                            expandedPanel === "tasks"
                              ? "Restore split view"
                              : "Expand tasks to full screen"
                          }
                          onClick={() =>
                            setExpandedPanel(
                              expandedPanel === "tasks" ? null : "tasks",
                            )
                          }
                        >
                          {expandedPanel === "tasks" ? "⊠" : "⤢"}
                        </button>
                      </div>
                      {(() => {
                        const today = new Date();
                        const todayStr = getLocalDateString(today);
                        const selectedTask = weekDays.find(
                          (day) => day.date === selectedDayDate,
                        );
                        return selectedTask ? (
                          <DayCard
                            key={selectedTask.date}
                            ref={(el) =>
                              (dayCardRefs.current[selectedTask.date] = el)
                            }
                            date={selectedTask.date}
                            dayName={selectedTask.displayName}
                            tasks={groupedTasks[selectedTask.date] || []}
                            categories={categories}
                            onAddTask={handleAddTask}
                            onToggleTask={handleToggleTask}
                            onDeleteTask={handleDeleteTask}
                            onDeleteDayTasks={handleDeleteDayTasks}
                            onToggleNotification={handleToggleNotification}
                            onCalendarChange={handleTaskCalendarChange}
                            onReorderTasks={handleReorderTasks}
                            isToday={selectedTask.date === todayStr}
                          />
                        ) : (
                          <div className="empty-state">
                            No tasks for this date
                          </div>
                        );
                      })()}
                    </div>
                    <div
                      className={`todos-section${expandedPanel === "todos" ? " panel-expanded" : expandedPanel === "tasks" ? " panel-hidden" : ""}`}
                    >
                      <div className="panel-toggle-bar">
                        <button
                          className="panel-toggle-btn"
                          title={
                            expandedPanel === "todos"
                              ? "Restore split view"
                              : "Expand todos to full screen"
                          }
                          onClick={() =>
                            setExpandedPanel(
                              expandedPanel === "todos" ? null : "todos",
                            )
                          }
                        >
                          {expandedPanel === "todos" ? "⊠" : "⤢"}
                        </button>
                      </div>
                      <TodoList
                        todos={todos}
                        onAddTodo={handleAddTodo}
                        onToggleTodo={handleToggleTodo}
                        onDeleteTodo={handleDeleteTodo}
                        onMarkTodoMissed={handleMarkTodoMissed}
                        onRescheduleTodo={handleRescheduleTodo}
                        onClearMissed={handleClearMissedTodos}
                        onDeleteAll={handleDeleteAllTodos}
                        onClearCompleted={handleClearCompletedTodos}
                        onEditTodo={handleEditTodo}
                        onTodoCalendarChange={handleTodoCalendarChange}
                        isAddingTodo={addingTodo}
                        togglingTodo={togglingTodo}
                        deletingTodo={deletingTodo}
                        onConnectCalendar={handleConnectCalendar}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="days-grid">
                    {weekDays.map((day) => (
                      <DayCard
                        key={day.date}
                        ref={(el) => (dayCardRefs.current[day.date] = el)}
                        date={day.date}
                        dayName={day.displayName}
                        tasks={groupedTasks[day.date] || []}
                        categories={categories}
                        onAddTask={handleAddTask}
                        onToggleTask={handleToggleTask}
                        onDeleteTask={handleDeleteTask}
                        onDeleteDayTasks={handleDeleteDayTasks}
                        onReorderTasks={handleReorderTasks}
                        onCalendarChange={handleTaskCalendarChange}
                        isAddingTask={addingTask}
                        deletingTask={deletingTask}
                        onHeaderClick={() => {
                          handleDateChange({ target: { value: day.date } });
                          setViewMode("day");
                        }}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === "categories" && (
          <div className="categories-view">
            <CategoryManager
              categories={categories}
              onAdd={handleAddCategory}
              onUpdate={handleUpdateCategory}
              onDelete={handleDeleteCategory}
              isAdding={addingCategory}
              updatingCategory={updatingCategory}
              deletingCategory={deletingCategory}
            />
          </div>
        )}

        {activeTab === "template" && (
          <div className="template-view">
            <TemplateSetup
              categories={categories}
              templates={templates}
              onCreateTemplate={handleCreateTemplate}
              onUpdateTemplate={handleUpdateTemplate}
              onDeleteTemplate={handleDeleteTemplate}
              onApplyTemplate={handleApplyTemplate}
              onTemplatesChanged={loadTemplates}
              isApplyingTemplate={loading}
              isCreatingTemplate={creatingTemplate}
              updatingTemplate={updatingTemplate}
              deletingTemplate={deletingTemplate}
            />
          </div>
        )}

        {activeTab === "analytics" && (
          <div className="analytics-view">
          <LiveInsightBanner />
          <div className="analytics-controls">
              <div className="analytics-type-selector">
                <button
                  className={`type-btn ${
                    analyticsType === "week" ? "active" : ""
                  }`}
                  onClick={() => setAnalyticsType("week")}
                >
                  Week View
                </button>
                <button
                  className={`type-btn ${
                    analyticsType === "month" ? "active" : ""
                  }`}
                  onClick={() => setAnalyticsType("month")}
                >
                  Month View
                </button>
                <button
                  className={`type-btn ${
                    analyticsType === "category" ? "active" : ""
                  }`}
                  onClick={() => setAnalyticsType("category")}
                >
                  Category View
                </button>
              </div>

              {/* Week View Selectors */}
              {analyticsType === "week" && (
                <div className="analytics-date-selectors">
                  <select
                    className="analytics-selector"
                    value={selectedAnalyticsYear}
                    onChange={(e) =>
                      handleAnalyticsYearChange(Number(e.target.value))
                    }
                  >
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() - 2 + i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                  <select
                    className="analytics-selector"
                    value={selectedAnalyticsMonth}
                    onChange={(e) =>
                      handleAnalyticsMonthChange(Number(e.target.value))
                    }
                  >
                    {[
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December",
                    ].map((month, index) => (
                      <option key={index} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>
                  <select
                    className="analytics-selector"
                    value={selectedAnalyticsWeek}
                    onChange={(e) =>
                      handleAnalyticsWeekChange(Number(e.target.value))
                    }
                  >
                    {[
                      ...Array(
                        getMaxWeeksInMonth(
                          selectedAnalyticsYear,
                          selectedAnalyticsMonth,
                        ),
                      ),
                    ].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        Week {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Month View Selectors */}
              {analyticsType === "month" && (
                <div className="analytics-date-selectors">
                  <select
                    className="analytics-selector"
                    value={selectedAnalyticsYear}
                    onChange={(e) =>
                      handleAnalyticsYearChange(Number(e.target.value))
                    }
                  >
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() - 2 + i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                  <select
                    className="analytics-selector"
                    value={selectedAnalyticsMonth}
                    onChange={(e) =>
                      handleAnalyticsMonthChange(Number(e.target.value))
                    }
                  >
                    {[
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December",
                    ].map((month, index) => (
                      <option key={index} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {analyticsType === "category" && (
                <select
                  className="category-filter"
                  value={selectedAnalyticsCategory}
                  onChange={(e) => setSelectedAnalyticsCategory(e.target.value)}
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.icon} {cat.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {analyticsData ? (
              <Analytics
                analytics={analyticsData}
                type={analyticsType}
                todos={todos}
                missedTodos={todos.filter(t => t.missed && !t.completed)}
                year={selectedAnalyticsYear}
                month={selectedAnalyticsMonth}
                week={selectedAnalyticsWeek}
              />
            ) : (
              <div className="empty-state-large">
                <div className="empty-icon">📊</div>
                <h3>Select Options to View Analytics</h3>
                <p>
                  Choose a view type and select a week/month to see your
                  progress
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <AssistantPanel
        selectedDate={selectedDate}
        selectedDayDate={selectedDayDate}
        activeTab={activeTab}
        viewMode={viewMode}
        onRefresh={handleAssistantRefresh}
      />

      {/* Template Modal - rendered via Portal to appear above all content */}
      {showTemplateModal &&
        ReactDOM.createPortal(
          <div
            className="modal-overlay"
            onClick={() => setShowTemplateModal(false)}
          >
            <div
              className="modal-content template-apply-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Select Template to Apply</h3>
              <div className="form-group">
                <label>Choose a template:</label>
                <select
                  value={selectedTemplateForApply}
                  onChange={(e) => setSelectedTemplateForApply(e.target.value)}
                  className="template-select"
                >
                  <option value="">Select a template...</option>
                  {templates.map((template) => (
                    <option key={template._id} value={template._id}>
                      {template.name} ({template.tasks?.length || 0} tasks)
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button
                  onClick={() => {
                    setShowTemplateModal(false);
                    setSelectedTemplateForApply("");
                  }}
                  className="btn-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyTemplate}
                  className="btn-save"
                  disabled={!selectedTemplateForApply || loading}
                >
                  {loading ? "Applying..." : "Apply Template"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Dashboard;
